import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import {
  RCIPWizardViewModel,
  QuoteBreakdownViewModel,
  EligibilityStatementRES,
  EndorsementViewModel,
  ProgramEnum,
  SubmissionStatusEnum,
  PolicyChangeQuoteViewModel,
} from '@program-suite/data-common'; // Adjust import path as needed

@Injectable({
  providedIn: 'root',
})
export class WizardService {
  private http = inject(HttpClient);

  //#region RCIP Wizard
  /**
   * Get wizard data
   * @param id The Submission GUID
   * @returns RCIPWizardViewModel containing wizard data
   * @throws Error if unauthorized or request fails
   */
  async getWizard(id: string): Promise<RCIPWizardViewModel> {
    try {
      return await firstValueFrom(this.http.get<RCIPWizardViewModel>(`RCIPWizard/${id}`));
    } catch (error) {
      throw new Error('Failed to get wizard data');
    }
  }

  /**
   * Get quote breakdown
   * @param id The Submission GUID
   * @returns QuoteBreakdownViewModel containing quote breakdown
   * @throws Error if unauthorized or request fails
   */
  async getQuoteBreakdown(id: string): Promise<QuoteBreakdownViewModel> {
    try {
      return await firstValueFrom(this.http.get<QuoteBreakdownViewModel>(`Submission/policy-pricing-totals/${id}`));
    } catch (error) {
      throw new Error('Failed to get quote breakdown');
    }
  }

  async getEligibilityStatements(id: string): Promise<EligibilityStatementRES> {
    try {
      return await firstValueFrom(this.http.get<EligibilityStatementRES>(`EligibilityStatement/${id}`));
    } catch (error) {
      throw new Error('Failed to get eligibility questions');
    }
  }

  async getEndorsements(
    programCode: ProgramEnum,
    stateCode: string,
    effectiveDate: string,
  ): Promise<EndorsementViewModel[]> {
    try {
      return await firstValueFrom(
        this.http.get<EndorsementViewModel[]>(`Submission/endorsements/${programCode}/${stateCode}/${effectiveDate}`),
      );
    } catch (error) {
      throw new Error('Failed to get endorsements');
    }
  }

  /**
   * Create new wizard
   * @param wizardData Wizard data to save
   * @param tenantId Optional tenant ID for program app (required for program API, not needed for wholesaler API)
   * @returns Submission Guid
   * @throws Error if unauthorized or save fails
   */
  async saveNewWizard(wizardData: RCIPWizardViewModel, tenantId?: number): Promise<string> {
    try {
      const url = tenantId ? `RCIPWizard/${tenantId}` : 'RCIPWizard';
      return await firstValueFrom(this.http.post<string>(url, wizardData));
    } catch (error) {
      // Preserve the original HttpErrorResponse so callers can surface validation messages
      throw error;
    }
  }

  /**
   * Rate a wizard
   * @param id The Submission GUID to rate
   * @returns QuoteBreakdownViewModel containing rate information
   * @throws Error if unauthorized, not ratable, or invalid data
   */
  async rateWizard(id: string): Promise<QuoteBreakdownViewModel> {
    try {
      return await firstValueFrom(this.http.post<QuoteBreakdownViewModel>(`RCIPWizard/Rate/${id}`, null));
    } catch (error: any) {
      // Preserve the original HttpErrorResponse so callers can surface validation messages
      throw error;
    }
  }

  /**
   * Update existing wizard
   * @param id The submission GUID to update
   * @param wizardData Updated wizard data
   * @returns void
   * @throws Error if unauthorized or update fails
   */
  async updateWizard(id: string, wizardData: RCIPWizardViewModel): Promise<void> {
    try {
      await firstValueFrom(this.http.put(`RCIPWizard/${id}`, wizardData));
    } catch (error) {
      // Preserve the original HttpErrorResponse so callers can surface validation messages
      throw error;
    }
  }

  /**
   * Create a submission
   * @param id The Submission GUID
   * @returns SubmissionStatusEnum containing submission status
   * @throws Error if unauthorized or submission fails
   */
  async createSubmission(id: string): Promise<SubmissionStatusEnum> {
    try {
      return await firstValueFrom(this.http.post<SubmissionStatusEnum>(`Submission/submit/${id}`, null));
    } catch (error) {
      throw new Error('Failed to create submission');
    }
  }

  /**
   * Get submission status
   * @param id The Submission GUID to check status
   * @returns SubmissionStatusEnum containing submission status
   * @throws Error if unauthorized or request fails
   */
  async getSubmissionStatus(id: string): Promise<SubmissionStatusEnum> {
    try {
      return await firstValueFrom(this.http.get<SubmissionStatusEnum>(`Submission/status/${id}`));
    } catch (error) {
      throw new Error('Failed to get submission status');
    }
  }
  //#endregion

  //#region RCIP Coverage Adjustment
    /**
   * Get Coverage Adjustment Wizard data
   * @param id The PolicyChangeGiud or PolicyGuid
   * @returns RCIPWizardViewModel containing wizard data
   * @throws Error if unauthorized or request fails
   */
  async getCoverageAdjustmentWizard(id: string): Promise<RCIPWizardViewModel> {
    try {
      return await firstValueFrom(this.http.get<RCIPWizardViewModel>(`policyChange/coverage-adjustment/${id}`));
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get Coverage Adjustment Quote Breakdown
   * @param policyChangeGuid
   * @param wizardData Wizard data to save
   * @returns PolicyChangeQuoteViewModel containing quote breakdown
   * @throws Error if unauthorized or save fails
   */
  async getCoverageAdjustmentQuote(policyChangeGuid: string): Promise<PolicyChangeQuoteViewModel> {
    try {
      return await firstValueFrom(this.http.get<PolicyChangeQuoteViewModel>(`policyChange/quote/${policyChangeGuid}`));
    } catch (error) {
      throw error;
    }
  }

  /**
   * Rate a wizard
   * @param model PolicyChangeViewModelREQ
   * @returns PolicyChangeViewModelREQ containing rate information
   * @throws Error if unauthorized, not ratable, or invalid data
   */
  async rateCoverageAdjustmentWizard(model: FormData): Promise<PolicyChangeQuoteViewModel> {
    try {
      return await firstValueFrom(this.http.post<PolicyChangeQuoteViewModel>(`policyChange/rerate`, model));
    } catch (error: any) {
      throw error;
    }
  }

  //#endregion
}
